import { delay } from 'utils/delay'
import capitalizeFirstLetterString from 'utils/capitalizeFirstLetter'
import { siteSpecieDerivationOfEstimate } from 'constants/siteSpecieDerivationOfEstimate'
import { NUMBER_OF_MATURE_INDIVIDUALS_ID, NUMBER_OF_LOCALITIES_ID } from 'constants/speciesParameterType'
import { requestUpdateSiteSpecieAssesmentParameter } from 'api/siteSpecieAssesmentParameter'
import { minBestMaxValidation } from 'utils/minBestMaxValidation'
import { isValidDecimalNumber, noZeroAfterDecimal } from 'utils/isValidDecimalNumber'
import { isValidYYYYFormat } from 'validators/isValidYYYYFormat'
import checkIsNumber from 'utils/checkIsNumber'
import {
  B_Value,
  B2_Value,
  B3_Value,
  B3C_Value,
  D1a_Value,
  D1b_Value,
  D2_Value,
  D3_Value,
  LEGACY_ONLY_Value,
  IBA_B1b_Value,
  IBA_B2b_Value,
  IBA_C2_Value,
  IBA_C3_Value,
  IBA_C6_Value,
  getIdCriteria,
  arrIdCriteria
} from 'flow/siteProposal/pages/assessment/components/speciesDetails/speciesDetailsModal/components/constants'
import { cleanLegacySelection, cleanMutuallyExclusivePairSelection } from 'utils/speciesParameterSiteSpeciesDataHelpers'

export const formattedDerivationOptions = siteSpecieDerivationOfEstimate.map((item) => ({
  value: item.id,
  label: capitalizeFirstLetterString(item.name)
}))

export const criteriaDefaultOptions = [
  { label: 'Site regularly holds trigger in one or more life cycle stages (A1, B1)', value: 1 },
  { label: 'Species is regularly occurring and is range-restricted in this season (B2)', value: 2 },
  {
    label:
      'Site regularly holds trigger in one or more life cycle stages, and is ecoregion or bioregion-restricted (B3a, B3b)',
    value: 3
  },
  { label: 'Site part of the globally most important 5% of occupied habitat (B3c)', value: 4 },
  { label: 'Site predictably holds aggregation in one or more life cycle stages (D1a)', value: 5 },
  { label: 'Site predictably holds one of 10 largest aggregations known for the species (D1b)', value: 6 },
  { label: 'Site supports ≥10% of the global population as a refugium during periods of environmental stress (D2)', value: 7 },
  { label: 'Site predictably acts as recruitment source maintaining ≥10% of the global population (D3)', value: 8 },
  { label: 'Site assessed against legacy criteria only.', value: true }
]

export const criteriaDefaultNoD1Options = [
  { label: 'Site regularly holds trigger in one or more life cycle stages (A1, B1)', value: 1 },
  { label: 'Species is regularly occurring and is range-restricted in this season (B2)', value: 2 },
  {
    label:
      'Site regularly holds trigger in one or more life cycle stages, and is ecoregion or bioregion-restricted (B3a, B3b)',
    value: 3
  },
  { label: 'Site part of the globally most important 5% of occupied habitat (B3c)', value: 4 },
  { label: 'Site supports ≥10% of the global population as a refugium during periods of environmental stress (D2)', value: 7 },
  { label: 'Site predictably acts as recruitment source maintaining ≥10% of the global population (D3)', value: 8 },
  { label: 'Site assessed against legacy criteria only.', value: true }
]

export const ibaCriteriaEUDefaultOptions = [
  {
    label:
      'Species has unfavourable conservation status in the region, and site is one of the top ‘n’ most important sites in the country (IBA B1b)',
    value: 1
  },
  {
    label:
      'Species population is concentrated in the region, and site is one of the top ‘n’ most important sites in the country (IBA B2a)',
    value: 2
  },
  { label: 'Species is listed on Annex I of EU Birds Directive (IBA C2)', value: 3 },
  { label: 'Species is migratory (IBA C3)', value: 4 },
  {
    label:
      'Species is listed on Annex I of EU Birds Directive, and site is one of the top five most important sites in the NUTS region (IBA C6)',
    value: 5
  }
]

export const ibaCriteriaDefaultOptions = [
  {
    label:
      'Species has unfavourable conservation status in the region, and site is one of the top ‘n’ most important sites in the country (IBA B1b)',
    value: 1
  },
  {
    label:
      'Species population is concentrated in the region, and site is one of the top ‘n’ most important sites in the country (IBA B2a)',
    value: 2
  },
  {
    label:
      'Species is listed on Annex I of EU Birds Directive, and site is one of the top five most important sites in the NUTS region (IBA C6)',
    value: 5
  }
]

const inputSaveDelayTime = 300
export const updateSourceTimeOut = delay(inputSaveDelayTime)
export const updateDerivationTimeOut = delay(inputSaveDelayTime)
export const updateNotesTimeOut = delay(inputSaveDelayTime)
export const updateCriteriaTimeOut = delay(inputSaveDelayTime)
export const updateJustificationTimeOut = delay(inputSaveDelayTime)
export const updateSourceHabitatTimeOut = delay(inputSaveDelayTime)
export const updateEvidenceTimeOut = delay(inputSaveDelayTime)
export const updateYearRecentTimeOut = delay(inputSaveDelayTime)
export const updateMinNumberTimeOut = delay(inputSaveDelayTime)
export const updateWhatAre10RuTimeOut = delay(inputSaveDelayTime)
export const updateSourceReproducTimeOut = delay(inputSaveDelayTime)

export const fields = {
  min: 'minSite',
  best: 'bestSite',
  max: 'maxSite'
}

export const getIbaCriteriaEUOptions = (selectedValues) => {
  const includesC2 = selectedValues.includes(IBA_C2_Value)
  const includesC3 = selectedValues.includes(IBA_C3_Value)

  return ibaCriteriaEUDefaultOptions.map((option) => {
    if (option.value === IBA_C2_Value && includesC3) {
      return { ...option, disabled: true }
    }
    if (option.value === IBA_C3_Value && includesC2) {
      return { ...option, disabled: true }
    }
    return { ...option, disabled: false }
  })
}

export const buildCriteriaPayload = (cleanedId) => {
  const isLegacyOnly = cleanedId.includes(LEGACY_ONLY_Value) && cleanedId.length === 1

  return {
    isLegacyOnly,
    payload: {
      regularlyHeld: !isLegacyOnly && cleanedId.includes(B_Value),
      regularlyHeldRestricted: !isLegacyOnly && cleanedId.includes(B3_Value),
      aggregationPredictability: !isLegacyOnly && cleanedId.includes(D1a_Value),
      aggregationsAsCriteria: !isLegacyOnly && cleanedId.includes(D1b_Value),
      refugium: !isLegacyOnly && cleanedId.includes(D2_Value),
      recruitmentSource: !isLegacyOnly && cleanedId.includes(D3_Value),
      regularlyOccurring: !isLegacyOnly && cleanedId.includes(B2_Value),
      globallyMostImportant: !isLegacyOnly && cleanedId.includes(B3C_Value),
      iba_only: isLegacyOnly
    }
  }
}

export const buildCriteriaOptions = (currentCriteriaSelection, assessmentParameter, siteBestGlobal) => {
  const hasLegacySelected = currentCriteriaSelection.includes(LEGACY_ONLY_Value)
  const hasOtherOptionsSelected = currentCriteriaSelection.some(
    (val) => val !== LEGACY_ONLY_Value && typeof val === 'number'
  )

  let baseOptions = criteriaDefaultNoD1Options
  if (assessmentParameter.value == NUMBER_OF_MATURE_INDIVIDUALS_ID) {
    baseOptions = !Number.isNaN(Number(siteBestGlobal)) && siteBestGlobal !== ''
      ? criteriaDefaultOptions.filter((criteria) => criteria.value !== D1b_Value)
      : criteriaDefaultOptions
  }

  return baseOptions.map((option) => (
    option.value === LEGACY_ONLY_Value
      ? { ...option, disabled: hasOtherOptionsSelected }
      : { ...option, disabled: hasLegacySelected }
  ))
}

export const getNextCriteriaSelectionState = ({ parameter, natureOfOccurrence }) => {
  const currentCriteria = getIdCriteria(parameter, arrIdCriteria)
  const hasLegacy = parameter.iba_only === true
  const currentStateHasLegacy = natureOfOccurrence.includes(LEGACY_ONLY_Value)

  if (hasLegacy && !currentStateHasLegacy) {
    return { shouldUpdate: true, nextValue: [LEGACY_ONLY_Value] }
  }
  if (!hasLegacy && currentStateHasLegacy) {
    return { shouldUpdate: true, nextValue: currentCriteria }
  }
  if (!hasLegacy && !currentStateHasLegacy) {
    const stateCriteria = natureOfOccurrence.filter((val) => typeof val === 'number')
    const hasChanged =
      stateCriteria.length !== currentCriteria.length ||
      !stateCriteria.every((val) => currentCriteria.includes(val))
    return { shouldUpdate: hasChanged, nextValue: currentCriteria }
  }

  return { shouldUpdate: false, nextValue: currentCriteria }
}

export const getIbaCriteriaOptionsByRegion = (isEuCountry) => {
  if (isEuCountry) {
    return ibaCriteriaDefaultOptions
  }
  return ibaCriteriaDefaultOptions.filter((option) => option.value !== 5)
}

export const updateCriteriaSelection = ({
  id,
  siteSpecieParameterId,
  siteHistoryId,
  species,
  updateParameterList,
  setNatureOfOccurrence,
  setSiteNatureOfOccurance,
  setSiteJustification,
  siteNatureOfOccurance,
  siteJustification,
  assessmentParameter
}) => {
  const cleanedId = cleanLegacySelection(id, LEGACY_ONLY_Value)
  setNatureOfOccurrence(cleanedId)
  const { isLegacyOnly, payload } = buildCriteriaPayload(cleanedId)

  requestUpdateSiteSpecieAssesmentParameter(
    siteSpecieParameterId,
    siteHistoryId,
    species.id,
    species.historyId,
    payload
  ).then((data) => {
    if (!isLegacyOnly) {
      if (cleanedId.includes(D1b_Value) && !cleanedId.includes(D1a_Value)) {
        setSiteNatureOfOccurance(true)
        setSiteJustification(false)
      }

      if (cleanedId.includes(D1a_Value) && !cleanedId.includes(D1b_Value)) {
        setSiteNatureOfOccurance(false)
        setSiteJustification(false)
      }

      if (!cleanedId.includes(D1a_Value) && !cleanedId.includes(D1b_Value)) {
        setSiteNatureOfOccurance(false)
        setSiteJustification(true)
      }

      if (
        (cleanedId.includes(B2_Value) || cleanedId.includes(D2_Value) || cleanedId.includes(D3_Value)) &&
        assessmentParameter.value == NUMBER_OF_MATURE_INDIVIDUALS_ID
      ) {
        setSiteJustification(false)
      }
    }

    if (cleanedId.length == 0) {
      setSiteNatureOfOccurance(!siteNatureOfOccurance)
      setSiteJustification(!siteJustification)
    }

    updateParameterList(data)
  })
}

export const updateIbaCriteriaSelection = ({
  id,
  siteSpecieParameterId,
  siteHistoryId,
  species,
  updateParameterList,
  setIbaConditions,
  setCurrentIbaSelection
}) => {
  const cleanedId = cleanMutuallyExclusivePairSelection(id, IBA_C2_Value, IBA_C3_Value)
  setIbaConditions(cleanedId)
  setCurrentIbaSelection(cleanedId)

  requestUpdateSiteSpecieAssesmentParameter(
    siteSpecieParameterId,
    siteHistoryId,
    species.id,
    species.historyId,
    {
      unfavourable: cleanedId.includes(IBA_B1b_Value),
      concentrated: cleanedId.includes(IBA_B2b_Value),
      annexListed: cleanedId.includes(IBA_C2_Value),
      migratory: cleanedId.includes(IBA_C3_Value),
      annexListedMi: cleanedId.includes(IBA_C6_Value)
    }
  ).then((data) => {
    updateParameterList(data)
  })
}

export const createSpeciesParameterHandlers = ({
  parameter,
  siteSpecieParameterId,
  siteHistoryId,
  species,
  updateParameterList,
  assessmentParameter,
  siteBestGlobal,
  minSite,
  bestSite,
  maxSite,
  maxYear,
  minYear,
  setCurrentIbaSelection,
  setCurrentCriteriaSelection,
  setNatureOfOccurrence,
  setSiteNatureOfOccurance,
  setSiteJustification,
  siteNatureOfOccurance,
  siteJustification,
  setYearRecent,
  setUnitsNumber,
  setComprised,
  setSourceReproduc,
  setEvidence,
  setJustification,
  setSourceHabitat,
  setMinYear,
  setMaxYear,
  setYearError,
  setMinSite,
  setBestSite,
  setMaxSite,
  setError,
  setSource,
  setDerivation,
  setNotes,
  setIbaConditions
}) => {
  const handleIbaSelectionChange = (newSelection) => {
    const cleanedSelection = cleanMutuallyExclusivePairSelection(newSelection, IBA_C2_Value, IBA_C3_Value)
    setCurrentIbaSelection(cleanedSelection)
  }

  const handleCriteriaSelectionChange = (input) => {
    if (!Array.isArray(input)) {
      return
    }
    const cleanedSelection = cleanLegacySelection(input, LEGACY_ONLY_Value)
    setCurrentCriteriaSelection(cleanedSelection)
  }

  const handleChangeCriteria = (id) => {
    updateCriteriaTimeOut(() => {
      updateCriteriaSelection({
        id,
        siteSpecieParameterId,
        siteHistoryId,
        species,
        updateParameterList,
        setNatureOfOccurrence,
        setSiteNatureOfOccurance,
        setSiteJustification,
        siteNatureOfOccurance,
        siteJustification,
        assessmentParameter
      })
    })
  }

  const handleChangeIbaCriteria = (id) => {
    updateCriteriaTimeOut(() => {
      updateIbaCriteriaSelection({
        id,
        siteSpecieParameterId,
        siteHistoryId,
        species,
        updateParameterList,
        setIbaConditions,
        setCurrentIbaSelection
      })
    })
  }

  const handleChangeYearRecent = (e) => {
    if (Number.isInteger(Number(e.target.value)) && e.target.value.length < 5) {
      setYearRecent(e.target.value)
    }
    if (isValidYYYYFormat(Number(e.target.value)) || e.target.value === '') {
      updateYearRecentTimeOut(() => {
        requestUpdateSiteSpecieAssesmentParameter(
          siteSpecieParameterId,
          siteHistoryId,
          species.id,
          species.historyId,
          { yearPresenceEvidence: e.target.value === '' ? '' : e.target.value }
        ).then(updateParameterList)
      })
    }
  }

  const handleChangeUnitsNumber = (e) => {
    if (checkIsNumber(e.target.value)) {
      setUnitsNumber(e.target.value)
      updateMinNumberTimeOut(() => {
        requestUpdateSiteSpecieAssesmentParameter(
          siteSpecieParameterId,
          siteHistoryId,
          species.id,
          species.historyId,
          { minReproductiveUnits: e.target.value === '' ? 0 : e.target.value }
        ).then(updateParameterList)
      })
    }
  }

  const handleChangeComprised = (e) => {
    setComprised(e.target.value)
    updateWhatAre10RuTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        siteHistoryId,
        species.id,
        species.historyId,
        { ruCompromisedOf: e.target.value }
      ).then(updateParameterList)
    })
  }

  const handleChangeSourceReproduc = (e) => {
    setSourceReproduc(e.target.value)
    updateSourceReproducTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        siteHistoryId,
        species.id,
        species.historyId,
        { ruSource: e.target.value }
      ).then(updateParameterList)
    })
  }

  const handleChangeEvidence = (e) => {
    setEvidence(e.target.value)
    updateEvidenceTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        siteHistoryId,
        species.id,
        species.historyId,
        { speciesPresenceEvidence: e.target.value }
      )
    })
  }

  const handleChangeJustification = (e) => {
    setJustification(e.target.value)
    updateJustificationTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        siteHistoryId,
        species.id,
        species.historyId,
        { justification: e.target.value === '' ? null : e.target.value }
      ).then(updateParameterList)
    })
  }

  const handleChangeSourceHabitat = (e) => {
    setSourceHabitat(e.target.value)
    updateSourceHabitatTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        siteHistoryId,
        species.id,
        species.historyId,
        { globallyMostImportant5: e.target.value === '' ? null : e.target.value }
      ).then(updateParameterList)
    })
  }

  const handleChangeMinYear = (e) => {
    if (Number.isInteger(Number(e.target.value)) && e.target.value.length < 5 && e.target.value <= new Date().getFullYear()) {
      setMinYear(e.target.value)
    }
    if (isValidYYYYFormat(Number(e.target.value)) || e.target.value === '') {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        siteHistoryId,
        species.id,
        species.historyId,
        { earliestDataSetYear: e.target.value === '' ? null : Number(e.target.value), latestDataSetYear: maxYear === '' ? null : Number(maxYear) }
      ).then((data) => {
        updateParameterList(data)
        setYearError({ field: '', error: '' })
      })
    }
  }

  const handleChangeMaxYear = (e) => {
    const input = e.target.value
    if (/^\d{0,4}$/.test(input)) {
      setMaxYear(input)
      if (input === '') {
        setYearError({ field: '', error: '' })
        return
      }
      if (input.length === 4) {
        const yearNum = Number(input)
        const currentYear = new Date().getFullYear()
        if (yearNum <= currentYear) {
          setYearError({ field: '', error: '' })
          requestUpdateSiteSpecieAssesmentParameter(
            siteSpecieParameterId,
            siteHistoryId,
            species.id,
            species.historyId,
            { earliestDataSetYear: minYear === '' ? null : Number(minYear), latestDataSetYear: yearNum }
          ).then(updateParameterList)
        } else {
          setYearError({ field: 'maxYear', error: 'Year cannot be in the future' })
        }
      }
    }
  }

  const handleChangeMinSite = (e) => {
    const errorMessage = minBestMaxValidation(e.target.value, bestSite, maxSite, fields, 'min')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setMinSite(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMinSite(e.target.value)
        setError(errorMessage)
      }
      const value = (
        (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
          parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
        e.target.value.includes('.')
      ) ? Number(Number.parseFloat(e.target.value).toFixed(0)) : Number(Number.parseFloat(e.target.value).toFixed(4))
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryId, species.id, species.historyId, {
        minSite: e.target.value ? value : null
      }).then(updateParameterList)
    }
  }

  const handleBlurMinSite = (e) => {
    if (
      (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
        parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
      e.target.value.includes('.')
    ) return
    const errorMessage = minBestMaxValidation(e.target.value, bestSite, maxSite, fields, 'min')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setMinSite(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMinSite(e.target.value)
        setError(errorMessage)
      }
      const value = Number(Number.parseFloat(e.target.value).toFixed(10))
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryId, species.id, species.historyId, {
        minSite: e.target.value ? value : null
      }).then(updateParameterList)
    }
  }

  const handleChangeBestSite = (e) => {
    const errorMessage = minBestMaxValidation(minSite, e.target.value, maxSite, fields, 'best')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setBestSite(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setBestSite(e.target.value)
        setError(errorMessage)
      }
      const value = (
        (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
          parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
        e.target.value.includes('.')
      ) ? Number(Number.parseFloat(e.target.value).toFixed(0)) : Number(Number.parseFloat(e.target.value).toFixed(4))
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryId, species.id, species.historyId, {
        bestSite: e.target.value ? value : null
      }).then(updateParameterList)
    }
  }

  const handleBlurBestSite = (e) => {
    if (
      (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
        parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
      e.target.value.includes('.')
    ) return
    const errorMessage = minBestMaxValidation(minSite, e.target.value, maxSite, fields, 'best')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setBestSite(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setBestSite(e.target.value)
        setError(errorMessage)
      }
      const value = Number(Number.parseFloat(e.target.value).toFixed(4))
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryId, species.id, species.historyId, {
        bestSite: e.target.value ? value : null
      }).then(updateParameterList)
    }
  }

  const handleChangeMaxSite = (e) => {
    const errorMessage = minBestMaxValidation(minSite, bestSite, e.target.value, fields, 'max')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setMaxSite(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMaxSite(e.target.value)
        setError(errorMessage)
      }
      const value = (
        (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
          parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
        e.target.value.includes('.')
      ) ? Number(Number.parseFloat(e.target.value).toFixed(0)) : Number(Number.parseFloat(e.target.value).toFixed(4))
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryId, species.id, species.historyId, {
        maxSite: e.target.value ? value : null
      }).then(updateParameterList)
    }
  }

  const handleBlurMaxSite = (e) => {
    if (
      (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
        parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
      e.target.value.includes('.')
    ) return
    const errorMessage = minBestMaxValidation(minSite, bestSite, e.target.value, fields, 'max')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setMaxSite(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMaxSite(e.target.value)
        setError(errorMessage)
      }
      const value = Number(Number.parseFloat(e.target.value).toFixed(4))
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryId, species.id, species.historyId, {
        maxSite: e.target.value ? value : null
      }).then(updateParameterList)
    }
  }

  const handleChangeSource = (e) => {
    setSource(e.target.value)
    updateSourceTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId, siteHistoryId, species.id, species.historyId, { sourceOfData: e.target.value }
      ).then(updateParameterList)
    })
  }

  const handleChangeDerivation = (e) => {
    setDerivation(e)
    updateDerivationTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId, siteHistoryId, species.id, species.historyId, { DerivationOfEstimateId: e.value }
      ).then(updateParameterList)
    })
  }

  const handleChangeNotes = (e) => {
    setNotes(e.target.value)
    updateNotesTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId, siteHistoryId, species.id, species.historyId, { notesOnSiteDataForSpecies: e.target.value }
      ).then(updateParameterList)
    })
  }

  return {
    handleIbaSelectionChange,
    handleCriteriaSelectionChange,
    handleChangeCriteria,
    handleChangeIbaCriteria,
    handleChangeYearRecent,
    handleChangeUnitsNumber,
    handleChangeComprised,
    handleChangeSourceReproduc,
    handleChangeEvidence,
    handleChangeJustification,
    handleChangeSourceHabitat,
    handleChangeMinYear,
    handleChangeMaxYear,
    handleChangeMinSite,
    handleBlurMinSite,
    handleChangeBestSite,
    handleBlurBestSite,
    handleChangeMaxSite,
    handleBlurMaxSite,
    handleChangeSource,
    handleChangeDerivation,
    handleChangeNotes
  }
}
